import { Button, DatePicker, Modal, Popconfirm, Select, Space, Tag, message } from "antd";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import dayjs from "dayjs";

import AlarmEventApi from "../../api/AlarmEventApi";
import DeviceApi from "../../api/DeviceApi";
import Table, { ColumnType } from "antd/es/table";
import StringUtils from "../../util/StringUtils";
import moment from "moment";
import { AlarmEventDetailsWrapper, AlarmEventManagementWrapper, Content, FilterBar, FilterItem, FilterItemLabel, InfoItem, InfoItemRow, InfoLabel, InfoValue, OperationWrapper, TableWrapper, Title, TitleWrapper, ToolBarWrapper } from "./AlarmEventManagement.styled";
import TimeUtils from "../../util/TimeUtils";
import { CheckCircleOutlined, SearchOutlined } from "@ant-design/icons";
import PermissionWrapper from "../../components/permission/PermissionWrapper";

interface AlarmEventManagementProps {

}

const AlarmEventManagement: React.FC<AlarmEventManagementProps> = (props)=> {
    // api
    let deviceApi = new DeviceApi();
    let alarmEventApi = new AlarmEventApi();
    let navigate = useNavigate();

    // state
    let [loading, setLoading] = useState(false);
    let [deviceId, setDeviceId] = useState(null);
    let [acked, setAcked] = useState(false);
    let [startTime, setStartTime] = useState(null);
    let [endTime, setEndTime] = useState(null);
    let [tableParams, setTableParams] = useState<any>({
        pagination: {
            current: 1,
            pageSize: 10,
            total: 0,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50],
        },
    });
    
    let [deviceOptions, setDeviceOptions] = useState(null);
    let [alarmEventViews, setAlarmEventViews] = useState([]);
    let [openDetailedEventModal, setOpenDetailedEventModal] = useState(false);
    let [detailedEventView, setDetailedEventView] = useState(null as any);

    // functions
    let loadDevices = async ()=> {
        let devices = (await deviceApi.getAll()).data.data;
        let deviceOptions = devices.map((e)=> {
            return {
                label: e.name,
                value: e.id,
            };
        });
        setDeviceOptions(deviceOptions);
    };

    let loadEventViews = async ()=> {
        // loading animation
        setLoading(true);
        
        // start/end time
        let startTimestamp = null;
        if (startTime!=null) {
            startTimestamp = dayjs(startTime).toDate().getTime();
        }
        let endTimestamp = null;
        if (endTime!=null) {
            endTimestamp = dayjs(endTime).toDate().getTime();
        }

        let alarmEventViewsPage = (await alarmEventApi.queryViews({
            deviceId: deviceId,
            acked: acked,
            startTime: startTimestamp,
            endTime: endTimestamp,
            pageNo: tableParams.pagination.current,
            pageSize: tableParams.pagination.pageSize,
        })).data.data;

        let newTableParams = {pagination: {...tableParams.pagination}};
        newTableParams.pagination.total = alarmEventViewsPage.totalElements;
        setTableParams(newTableParams);
        setAlarmEventViews(alarmEventViewsPage.elements);
        setLoading(false);
    };

    // events
    let onDeviceChange=(selectedValue: string)=> {
        setDeviceId(selectedValue);
    };

    let onAckedChange=(selectedValue: any)=> {
        setAcked(selectedValue);
    };

    let onDateTimeRangeChange = (dateTimeRangeValue: any[])=> {
        setStartTime(dateTimeRangeValue[0]);
        setEndTime(dateTimeRangeValue[1]);
    };

    let viewEventDetails = (eventView)=> {
        setDetailedEventView(eventView);
        setOpenDetailedEventModal(true);
    };

    let onQueryButtonClick = async ()=> {
        let pagination = tableParams.pagination;
        pagination.current = 1;
        tableParams.pagination = pagination;
        setTableParams({pagination: pagination});
        await loadEventViews();
    };

    let onTableChange = (pagination)=> {
        tableParams.pagination = pagination;
        setTableParams({pagination: pagination});
        loadEventViews();
    };

    let onAckAlarmEvent = async (alarmEventView)=> {
        await alarmEventApi.ack(alarmEventView.event.id);
        message.success('事件标记成功');
    };

    let onQueryDeviceMetrics = (alarmEventView)=> {
        navigate(`/device-management/device/${alarmEventView.event.deviceId}?tab=device-metrics-history&startTime=${alarmEventView.event.startTime}&endTime=${alarmEventView.event.endTime}`);
    };

    // effect
    useEffect(()=> {
        loadDevices();
        loadEventViews();
    }, []);
    
    // column definition
    let columns: ColumnType<any>[] = [
        {
            title: '设备名称',
            dataIndex: 'device.name',
            key: "device.name",
            align: 'center',
            render: (text, record, index)=> {
                return StringUtils.emptyToSlash(record.device?.name);
            }
        },
        {
            title: '规则名称',
            dataIndex: 'alarmRule.name',
            key: "alarmRule.name",
            align: 'center',
            render: (text, record, index)=> {
                return StringUtils.emptyToSlash(record.alarmRule?.name);
            }
        },
        {
            title: '事件详情',
            dataIndex: 'alarmRule.details',
            key: "alarmRule.details",
            align: 'center',
            render: (text, record, index)=> {
                return StringUtils.emptyToSlash(record.event?.details);
            }
        },
        {
            title: '事件时间',
            dataIndex: 'event.startTime',
            key: 'event.startTime',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{ moment(parseInt(record.event.startTime)).format("YYYY-MM-DD HH:mm:ss") }</span>
            }
        },
        {
            title: '持续时间',
            dataIndex: 'event.endTime',
            key: 'event.endTime',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{ TimeUtils.durationToText(record.event.endTime - record.event.startTime) }</span>
            }
        },
        {
            title: '事件状态',
            dataIndex: 'event.acked',
            key: "event.acked",
            align: 'center',
            render: (text, record, index)=> {
                return record.event.acked ? <Tag color="green">已处理</Tag> : <Tag color="red">未处理</Tag>
            }
        },
        {
            title: '操作',
            key: 'action',
            align: 'center',
            render: (text, record, index)=> {
                return <Space size="middle">
                    <a onClick={()=> viewEventDetails(record)}>查看详情</a>
                    <PermissionWrapper permissionCodePath="alarm-management.alarm-events.ack-alarm-events">
                        { !record.event.acked && 
                            <Popconfirm
                                    title="标记告警事件为已处理"
                                    description={ <div style={{paddingTop: "10px", paddingBottom: '10px'}}>确认要将此事件标记为已处理么?</div> }
                                    onConfirm={()=> onAckAlarmEvent(record)}
                                    okText="确定"
                                    cancelText="取消"
                                >
                                <a>标记为已处理</a>
                            </Popconfirm>
                        }
                    </PermissionWrapper>
                </Space>
            }
        }
    ];

    // render
    return (
        <AlarmEventManagementWrapper>
            <ToolBarWrapper>
                <FilterBar>
                    <Space size="middle">
                        <FilterItem>
                            <FilterItemLabel>设备名称</FilterItemLabel>
                            <Select
                                showSearch
                                allowClear
                                optionFilterProp="label"
                                filterSort={(a, b) => a <= b ? -1 : 1}
                                style={{ width: '150px' }}
                                placeholder="请选择目标设备"
                                defaultValue={deviceId}
                                value={deviceId}
                                onChange={onDeviceChange}
                                options={deviceOptions}
                            />
                        </FilterItem>

                        <FilterItem>
                            <FilterItemLabel>时间范围</FilterItemLabel>
                            <DatePicker.RangePicker
                                showTime={{ format: 'HH:mm:ss' }}
                                format="YYYY-MM-DD HH:mm:ss"
                                defaultValue={[dayjs().add(-1, 'd'), dayjs()]}
                                value={[startTime, endTime]}
                                allowEmpty={[false, true]}
                                onOk={onDateTimeRangeChange}
                            />
                        </FilterItem>

                        <FilterItem>
                            <FilterItemLabel>事件状态</FilterItemLabel>
                            <Select
                                showSearch
                                allowClear
                                optionFilterProp="label"
                                style={{ width: '100px' }}
                                placeholder="请选择事件状态"
                                defaultValue={acked}
                                value={acked}
                                onChange={onAckedChange}
                                options={[
                                    {label: '已处理', value: true},
                                    {label: '未处理', value: false}
                                ]}
                            />
                        </FilterItem>
                    </Space>

                    <Space size="large">
                        <Button 
                            type="primary" 
                            icon={<SearchOutlined style={{marginTop: '3px'}}/>} 
                            onClick={onQueryButtonClick}
                            className="query-button"
                        >查询</Button>
                    </Space>
                </FilterBar>
            </ToolBarWrapper>

            <TableWrapper>
                <Table
                    rowKey={(record)=> record.event.id}
                    columns={columns}
                    pagination={tableParams.pagination}
                    dataSource={alarmEventViews}
                    loading={loading}
                    onChange={onTableChange}
                />

                <Modal
                    open={openDetailedEventModal} 
                    onClose={()=> setOpenDetailedEventModal(false)} 
                    footer={null} 
                    closable={false}
                    width={600}
                >
                
                { detailedEventView && <AlarmEventDetailsWrapper>
                        <TitleWrapper>
                            <Title>事件详情</Title>
                        </TitleWrapper>

                        <Content>
                            <InfoItemRow>
                                <InfoItem>
                                    <InfoLabel>设备名称</InfoLabel>
                                    <InfoValue>{StringUtils.emptyToSlash(detailedEventView.device?.name)}</InfoValue>
                                </InfoItem>

                                <InfoItem>
                                    <InfoLabel>设备编号</InfoLabel>
                                    <InfoValue>{StringUtils.emptyToSlash(detailedEventView.device?.deviceNo)}</InfoValue>
                                </InfoItem>
                            </InfoItemRow>

                            <InfoItemRow>
                                <InfoItem>
                                    <InfoLabel>序列号SN</InfoLabel>
                                    <InfoValue>{StringUtils.emptyToSlash(detailedEventView.node?.sn)}</InfoValue>
                                </InfoItem>

                                <InfoItem>
                                    <InfoLabel>产品型号</InfoLabel>
                                    <InfoValue>{StringUtils.emptyToSlash(detailedEventView.node?.model)}</InfoValue>
                                </InfoItem>
                            </InfoItemRow>

                            <InfoItemRow>
                                <InfoItem>
                                    <InfoLabel>事件时间</InfoLabel>
                                    <InfoValue>{moment(parseInt(detailedEventView.event.startTime)).format('YYYY/MM/DD HH:mm:ss')}</InfoValue>
                                </InfoItem>

                                <InfoItem>
                                    <InfoLabel>持续时间</InfoLabel>
                                    <InfoValue>{TimeUtils.durationToText(detailedEventView.event.endTime - detailedEventView.event.startTime)}</InfoValue>
                                </InfoItem>
                            </InfoItemRow>

                            <InfoItemRow>
                                <InfoItem>
                                    <InfoLabel>事件状态</InfoLabel>
                                    <InfoValue>{detailedEventView.event.acked ? <Tag color="green">已处理</Tag> : <Tag color="red">未处理</Tag>}</InfoValue>
                                </InfoItem>

                                <InfoItem>
                                    <InfoLabel>处理时间</InfoLabel>
                                    <InfoValue>{detailedEventView.event.acked ? moment(parseInt(detailedEventView.event.ackTime)).format('YYYY/MM/DD HH:mm:ss') : 'N/A'}</InfoValue>
                                </InfoItem>
                            </InfoItemRow>

                            <InfoItemRow>
                                <InfoItem>
                                    <InfoLabel>相关数据</InfoLabel>
                                    <InfoValue>
                                        <a onClick={()=> onQueryDeviceMetrics(detailedEventView)}>查询相关数据</a>
                                    </InfoValue>
                                </InfoItem>
                            </InfoItemRow>
                        </Content>

                        <OperationWrapper>
                            <Space size="middle">
                                { !detailedEventView.event.acked && <Button type="default" icon={<CheckCircleOutlined />} color="green" onClick={()=> onAckAlarmEvent(detailedEventView)}>标记为已处理</Button> }
                                <Button type="primary" onClick={()=> setOpenDetailedEventModal(false)}>确定</Button>
                            </Space>
                        </OperationWrapper>
                    </AlarmEventDetailsWrapper>
                }
                </Modal>
            </TableWrapper>
        </AlarmEventManagementWrapper>
    );
}

export default AlarmEventManagement;
